import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { activityListQuery } from '@xdocs/schemas';
import type { AuditEvent, ErrorAlert } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' };

export interface UseActivityListResult {
  rows: AuditEvent[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
}

/**
 * F49 `/activity` — server-side pagination over `activity.list`
 * (user-scoped `audit_events` read, same `<DataTable>`-bridging shape as
 * `useSharesList`). `activityListQuery` has no dedicated `action`-type
 * filter field (only generic `q`) — the page's action-type dropdown sets
 * `query.q` to the closed action-enum value, matched server-side (E6 §5.9:
 * "q matched server-side; ILIKE ok for small catalogs") rather than
 * client-side filtering an already-fetched page (banned, §15).
 */
export function useActivityList(): UseActivityListResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected' && userId !== null;
  const queryKey = useMemo(() => ['activity', 'list', query] as const, [query]);

  const result = useQuery({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'activity.list', activityListQuery.parse(query)),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;
  const error = result.error as ErrorAlert | null;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? (error?.message ?? undefined) : undefined,
    onRetry: () => void result.refetch(),
  };
}
